import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Drawer  // Add this import
} from '@mui/material';
import { Menu as MenuIcon, ChevronLeft as ChevronLeftIcon } from '@mui/icons-material';
import { adminAuth, getUsers, updateUserRole, resetUserPassword } from '../api/adminApis';
import axios from 'axios';

const drawerWidth = 240;

const Admin = ({ username: propUsername, token: propToken }) => {
  const [open, setOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [username, setUsername] = useState(propUsername || '');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(propToken || '');
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (propUsername && propToken) {
      setUsername(propUsername);
      setToken(propToken);
      checkAuth(propUsername, propToken);
    }
  }, [propUsername, propToken]);

  const checkAuth = async (user, userToken) => {
    try {
      setIsLoading(true);
      await adminAuth({ username: user, token: userToken });
      setIsAuthenticated(true);
      fetchUsers(user, userToken);
    } catch (error) {
      setError('Admin access denied');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Use the correct parameter names that apollo login expects
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/apollo/login`, {
        user: username,     // Changed from 'username' to 'user'
        password: password
      });

      if (response.data.token) {
        setToken(response.data.token);
        await checkAuth(username, response.data.token);
      } else {
        setError('Invalid credentials or insufficient privileges');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed or admin access denied');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async (user = username, userToken = token) => {
    try {
      const usersData = await getUsers({ username: user, token: userToken });
      setUsers(usersData);
    } catch (error) {
      setError('Failed to fetch users');
    }
  };

  const handleRoleChange = async (targetUsername, newRole) => {
    try {
      await updateUserRole(targetUsername, newRole, { username, token });
      setSuccess(`Role updated for ${targetUsername}`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update role');
    }
  };

  const handlePasswordReset = async (targetUsername) => {
    try {
      await resetUserPassword(targetUsername, { username, token });
      setSuccess(`Password reset for ${targetUsername}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to reset password');
    }
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Admin Login
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', width: '100%', p: 3 }}>
      <CssBaseline />
      {/* Top App Bar */}
      <AppBar position="fixed" sx={{ width: `calc(100% - ${open ? drawerWidth : 0}px)`, ml: `${open ? drawerWidth : 0}px` }}>
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap>
            Admin Panel
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        anchor="left"
        open={open}
      >
        <Toolbar>
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Box sx={{ padding: 2 }}>
          <Typography variant="h6">Sidebar Content</Typography>
          <Button variant="contained">Option 1</Button>
          <Button variant="contained" sx={{ mt: 2 }}>Option 2</Button>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          padding: 3
        }}
      >
        <Toolbar />
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Users" />
            <Tab label="Settings" />
            <Tab label="Logs" />
          </Tabs>
        </Box>

        {currentTab === 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Access Role</TableCell>
                  <TableCell>Beta Access</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.username}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={user.access_role}
                          onChange={(e) => handleRoleChange(user.username, e.target.value)}
                        >
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="pcp">PCP</MenuItem>
                          <MenuItem value="mmd">MMD</MenuItem>
                          <MenuItem value="ma">MA</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>{user.beta ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{user.token_created_at || 'Never'}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handlePasswordReset(user.username)}
                      >
                        Reset Password
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {currentTab === 1 && (
          <Typography>Settings tab - Coming soon</Typography>
        )}

        {currentTab === 2 && (
          <Typography>Logs tab - Coming soon</Typography>
        )}
      </Box>
    </Box>
  );
};

export default Admin;
